import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../lib/utils'

// Ported from beUI's motion loaders (https://beui.dev/components/motion/loader).
// Two variants — a rotating arc (`spinner`) and a staggered bouncing trio (`dots`).
// Both draw in `currentColor`, so the parent sets the tint (e.g. es-green over a
// meal-image placeholder), and both scale from a single `size` prop. Under
// `prefers-reduced-motion` every transform is dropped for a calm opacity pulse,
// matching the reduced-motion behavior of our other motion components (accordion,
// popover). Used to signal "a real photo is on the way" while the async image
// pipeline is still generating a meal's image (#911).

export type LoaderVariant = 'spinner' | 'dots'

interface LoaderProps {
  /** `spinner` = rotating arc; `dots` = bouncing trio. */
  variant?: LoaderVariant
  /** Overall size in px. The variant scales its internals from this. */
  size?: number
  className?: string
  /** Announced to assistive tech; the element carries `role="status"`. */
  'aria-label'?: string
}

const DOT_IDS = ['a', 'b', 'c'] as const

export function Loader({ variant = 'spinner', size = 32, className, 'aria-label': ariaLabel = 'Loading' }: LoaderProps) {
  const reduce = useReducedMotion()
  return (
    <output aria-label={ariaLabel} className={cn('inline-flex items-center justify-center', className)}>
      {variant === 'dots' ? <Dots size={size} reduce={!!reduce} /> : <Spinner size={size} reduce={!!reduce} />}
    </output>
  )
}

// A faint full ring with a bright quarter-arc rotating over it. `pathLength={1}`
// lets the arc be a clean 25% of the circle regardless of radius.
function Spinner({ size, reduce }: { size: number; reduce: boolean }) {
  const stroke = Math.max(2, size / 11)
  const arc = (
    <>
      <circle cx={12} cy={12} r={9} stroke="currentColor" strokeWidth={stroke} className="opacity-20" />
      <circle
        cx={12}
        cy={12}
        r={9}
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinecap="round"
        pathLength={1}
        strokeDasharray="0.25 1"
      />
    </>
  )

  if (reduce) {
    return (
      <motion.svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 1.4, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}>
        {arc}
      </motion.svg>
    )
  }

  return (
    <motion.svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      style={{ transformOrigin: 'center' }}
      animate={{ rotate: 360 }}
      transition={{ duration: 0.8, repeat: Number.POSITIVE_INFINITY, ease: 'linear' }}>
      {arc}
    </motion.svg>
  )
}

// Three dots bouncing in sequence; reduced motion swaps the bounce for a shared
// opacity pulse (no vertical travel).
function Dots({ size, reduce }: { size: number; reduce: boolean }) {
  const dot = Math.max(4, size / 3.5)
  return (
    <span className="inline-flex items-end" style={{ gap: dot * 0.5 }}>
      {DOT_IDS.map((id, i) => (
        <motion.span
          key={id}
          className="rounded-full bg-current"
          style={{ width: dot, height: dot }}
          animate={reduce ? { opacity: [0.4, 1, 0.4] } : { y: [0, -dot * 0.6, 0] }}
          transition={{
            duration: reduce ? 1.4 : 0.6,
            repeat: Number.POSITIVE_INFINITY,
            ease: 'easeInOut',
            delay: reduce ? 0 : i * 0.12
          }}
        />
      ))}
    </span>
  )
}
